import { Observable } from 'rxjs';
import { ApiListResponse } from '../core/response-models/api-list-response.model';
import { ApiResponse } from '../core/response-models/api-response.model';
import { Page } from '../core/response-models/page.model';
import { Paging } from '../core/response-models/paging.model';
import { ApiService } from './api.service';
import { Base } from './base.model';
import { RequestOptions } from './request-options';

/**
 * Base for services that own one Hub resource with an entity model. Adds model building and CRUD to
 * `ApiService`, which owns the URL building, envelope unwrapping and `ApiError` mapping.
 *
 * Concrete subclasses add `@Injectable({ providedIn: 'root' })` and call `super('<endpoint>', ModelClass)`,
 * mirroring the hub's `ServiceBase` `modelType`. Endpoints under the resource that don't return the model
 * (stats, action results) use the inherited `request<R>()`.
 */
export abstract class BaseService<T extends Base> extends ApiService {
  constructor(endpoint: string, private readonly modelType: new (data: unknown) => T) {
    super(endpoint);
  }

  /** GET `/{prefix}/{endpoint}/{id}/{path}`. Emits `null` when the Hub returns no data. */
  get(id: string, opts: RequestOptions = {}): Observable<T | null> {
    return this.send(
      () => this.http.get<ApiResponse<unknown>>(this.buildUrl(opts, id), this.requestOptions(opts)),
      body => this.toEntity(body)
    );
  }

  /** GET `/{prefix}/{endpoint}/{path}` and map each item. Emits `[]` when the Hub returns no array. */
  getMany(opts: RequestOptions = {}): Observable<T[]> {
    return this.send(
      () => this.http.get<ApiResponse<unknown>>(this.buildUrl(opts), this.requestOptions(opts)),
      body => this.toEntities(body)
    );
  }

  /**
   * GET a paged list. Paging params go in `opts.query` because Hub endpoints differ (`limit` vs `pageSize`).
   * Without a `paging` block in the response, the whole result is treated as a single page.
   */
  getPage(opts: RequestOptions = {}): Observable<Page<T>> {
    return this.send(
      () => this.http.get<ApiListResponse<unknown>>(this.buildUrl(opts), this.requestOptions(opts)),
      body => this.toPage(body)
    );
  }

  /** POST the caller's body (minus server-owned keys) to `/{prefix}/{endpoint}/{path}`. */
  create(body: unknown, opts: RequestOptions = {}): Observable<T | null> {
    return this.send(
      () => this.http.post<ApiResponse<unknown>>(this.buildUrl(opts), this.stripServerOwnedKeys(body), this.requestOptions(opts)),
      response => this.toEntity(response)
    );
  }

  /** PUT the caller's body (minus server-owned keys) to `/{prefix}/{endpoint}/{id}/{path}`. */
  update(id: string, body: unknown, opts: RequestOptions = {}): Observable<T | null> {
    return this.send(
      () => this.http.put<ApiResponse<unknown>>(this.buildUrl(opts, id), this.stripServerOwnedKeys(body), this.requestOptions(opts)),
      response => this.toEntity(response)
    );
  }

  /** DELETE `/{prefix}/{endpoint}/{id}/{path}`. */
  delete(id: string, opts: RequestOptions = {}): Observable<void> {
    return this.send(
      () => this.http.delete<ApiResponse<unknown>>(this.buildUrl(opts, id), this.requestOptions(opts)),
      () => undefined
    );
  }

  /** Builds a model from one item of Hub `data`, as the hub's `ServiceBase.createType` does with knex rows. */
  protected createType(data: unknown): T {
    return new this.modelType(data);
  }

  // Bodies are typed `ApiResponse<unknown>` on purpose: the HttpClient generic is only a cast, and raw JSON
  // isn't a `T` until `createType` builds it (the hub does the same with knex rows).
  private toEntity(body: ApiResponse<unknown> | null): T | null {
    const data = body?.data;
    return data === null || data === undefined ? null : this.createType(data);
  }

  private toEntities(body: ApiResponse<unknown> | null): T[] {
    const data = body?.data;
    return Array.isArray(data) ? data.map(item => this.createType(item)) : [];
  }

  private toPage(body: ApiListResponse<unknown> | null): Page<T> {
    const items = this.toEntities(body);
    return { items, paging: body?.paging ?? singlePage(items.length) };
  }
}

function singlePage(count: number): Paging {
  return { page: 1, pageSize: count, totalItems: count, totalPages: 1 };
}
